import { createApi, type KyInstance } from '@enterprise/api';
import { isDefined, isNumber, SECOND } from '@enterprise/core';

import { getEnvVar } from '@/utilities/get-env-var';

import { InstanceType } from './enums/instance-type.enum';
import { authenticationRequired } from './hooks/authentication-required';
import { forbidden } from './hooks/forbidden';

/**
 * Creates an API instance based on the provided {@link InstanceType}.
 *
 * Depending on the instance type, this function initializes the API with the corresponding server URI
 * from environment variables:
 * - {@link InstanceType.Datastream}: Uses `VITE_APP_DATASTREAM_SERVER`
 * - {@link InstanceType.Enterprise}: Uses `VITE_APP_ENTERPRISE_API_URI`
 *
 * @param instanceType - The type of instance to create.
 * @returns The API instance configured for the specified instance type.
 */
export const createApiInstance = (instanceType: InstanceType) => {
  const timeout = getEnvVar('VITE_APP_API_TIMEOUT_IN_SECONDS');
  let instance: KyInstance;

  switch (instanceType) {
    case InstanceType.Datastream:
      instance = createApi(getEnvVar('VITE_APP_DATASTREAM_SERVER'));
      break;

    case InstanceType.Enterprise:
      instance = createApi(getEnvVar('VITE_APP_ENTERPRISE_API_URI'), {
        beforeError: [authenticationRequired, forbidden],
      });
      break;
  }

  if (isDefined(timeout) && isNumber(timeout)) {
    const seconds = Number.parseFloat(timeout);

    instance = instance.extend({
      timeout: seconds * SECOND,
    });
  }

  return instance;
};
